/**
 * MainLayout - Responsive shell layout for the Journal App.
 *
 * Implements responsive behavior across breakpoints:
 * - Mobile (375–767px): single column, calendar above editor, touch targets ≥44px
 * - Tablet (768–1023px): sidebar collapsed by default
 * - Desktop (1024–2560px): sidebar + content side-by-side
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5
 */

import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { detectBrowser, getSupportedBrowsersMessage, type BrowserInfo } from '@/utils/browserDetection';

// ─── Lazy-loaded route components for code splitting (Requirement 6.4: <3s initial load) ───
export const LazyCalendarView = lazy(() =>
  import('@/components/calendar/CalendarView').then((m) => ({ default: m.CalendarView }))
);
export const LazyEntryEditor = lazy(() =>
  import('@/components/editor/EntryEditor').then((m) => ({ default: m.EntryEditor }))
);

// ─── Props ───────────────────────────────────────────────────────────────────

export interface MainLayoutProps {
  /** Content to render in the sidebar slot. */
  sidebar?: ReactNode;
  /** Content to render in the main content slot. */
  children: ReactNode;
}

// ─── Loading Fallback ────────────────────────────────────────────────────────

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full min-h-[200px]" role="status" aria-label="Loading content">
      <div className="animate-pulse flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary-light" />
        <div className="w-24 h-3 rounded-full bg-primary-light" />
      </div>
    </div>
  );
}

// ─── Unsupported Browser Banner ──────────────────────────────────────────────

function UnsupportedBrowserBanner({ browserInfo }: { browserInfo: BrowserInfo }) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || browserInfo.isSupported) return null;

  return (
    <div
      role="alert"
      className="bg-amber-50 border-b border-amber-200 px-4 py-3 text-sm text-amber-900 flex items-center justify-between gap-4"
    >
      <p>
        Your browser ({browserInfo.name} {browserInfo.version}) may not be fully supported.
        For the best experience, please use one of: {getSupportedBrowsersMessage()}.
      </p>
      <button
        onClick={() => setDismissed(true)}
        className="shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-sm text-amber-700 hover:text-amber-900 transition-colors duration-fast"
        aria-label="Dismiss browser warning"
      >
        ✕
      </button>
    </div>
  );
}

// ─── Sidebar Toggle Button ───────────────────────────────────────────────────

function SidebarToggle({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-sm bg-bg-card border border-border shadow-card text-text-secondary hover:bg-hover-bg hover:text-text-primary transition-colors duration-fast"
      aria-label={isOpen ? 'Close sidebar' : 'Open sidebar'}
      aria-expanded={isOpen}
    >
      <svg
        className={`w-5 h-5 transition-transform duration-fast ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>
  );
}

// ─── MainLayout Component ────────────────────────────────────────────────────

export function MainLayout({ sidebar, children }: MainLayoutProps) {
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const [browserInfo, setBrowserInfo] = useState<BrowserInfo>({ name: '', version: 0, isSupported: true });

  // Detect browser on mount
  useEffect(() => {
    setBrowserInfo(detectBrowser());
  }, []);

  // On tablet, start with sidebar collapsed
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    if (mediaQuery.matches && sidebarOpen) {
      toggleSidebar();
    }
    // Only run on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-bg-primary flex flex-col overflow-x-hidden max-w-[2560px] mx-auto">
      {/* Unsupported browser banner (Requirement 6.5) */}
      <UnsupportedBrowserBanner browserInfo={browserInfo} />

      {/* Mobile layout (375–767px): single column, calendar above editor */}
      <div className="flex flex-col tablet:hidden w-full">
        {/* Mobile header with toggle */}
        <header className="sticky top-0 z-20 bg-bg-primary border-b border-border px-4 py-2 flex items-center justify-between">
          <h1 className="text-heading-3 text-text-heading font-semibold">Journal</h1>
          <SidebarToggle isOpen={sidebarOpen} onToggle={toggleSidebar} />
        </header>

        {/* Mobile sidebar content (collapsible, shown above main content) */}
        {sidebarOpen && sidebar && (
          <div className="border-b border-border bg-bg-card p-4 view-transition-fast">
            <Suspense fallback={<LoadingFallback />}>
              {sidebar}
            </Suspense>
          </div>
        )}

        {/* Mobile main content */}
        <main className="flex-1 p-4 w-full" role="main">
          <Suspense fallback={<LoadingFallback />}>
            {children}
          </Suspense>
        </main>
      </div>

      {/* Tablet + Desktop layout (768px+): sidebar + content side-by-side */}
      <div className="hidden tablet:flex tablet:flex-row flex-1 w-full">
        {/* Sidebar */}
        <aside
          className={`
            bg-bg-card border-r border-border flex-shrink-0
            transition-all duration-default ease-ease-default overflow-hidden
            ${sidebarOpen ? 'w-[280px] desktop:w-[320px]' : 'w-0'}
          `}
          aria-label="Sidebar navigation"
        >
          {sidebarOpen && (
            <div className="w-[280px] desktop:w-[320px] h-full overflow-y-auto p-4">
              <Suspense fallback={<LoadingFallback />}>
                {sidebar}
              </Suspense>
            </div>
          )}
        </aside>

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Tablet/Desktop header */}
          <header className="sticky top-0 z-20 bg-bg-primary border-b border-border px-6 py-3 flex items-center gap-4">
            <SidebarToggle isOpen={sidebarOpen} onToggle={toggleSidebar} />
            <h1 className="text-heading-3 text-text-heading font-semibold">Journal</h1>
          </header>

          {/* Content area */}
          <main className="flex-1 p-6 overflow-y-auto" role="main">
            <Suspense fallback={<LoadingFallback />}>
              {children}
            </Suspense>
          </main>
        </div>
      </div>
    </div>
  );
}
